export const AUTH_TOKEN_KEY = "Bearer";
export const ADMIN_AUTH_TOKEN_KEY = "admin-bearer";
export const TENANT_SWITCH_CONTEXT_KEY = "tenant-switch-context";
const LEGACY_AUTH_TOKEN_KEY = "PMSToken";

export interface TenantSwitchContext {
  projectId: string;
  projectNo: string;
  projectName: string;
  from: string;
  entryTime: string;
}

type UserLike = {
  token?: string;
  accessToken?: string;
  authorization?: string;
  Bearer?: string;
} | null;

export const normalizeAuthorizationToken = (value: unknown): string => {
  if (value === undefined || value === null) return "";
  const raw = String(value).trim();
  if (!raw) return "";
  const token = raw.replace(/^Bearer\s+/i, "").trim();
  if (!token) return "";
  return `Bearer ${token}`;
};

export const resolveTokenFromUser = (user: UserLike): string => {
  if (!user) return "";
  return normalizeAuthorizationToken(
    user.token ||
      user.accessToken ||
      user.authorization ||
      user.Bearer ||
      ""
  );
};

export const getCurrentAuthToken = (): string => {
  const current = normalizeAuthorizationToken(localStorage.getItem(AUTH_TOKEN_KEY));
  if (current) return current;

  const legacy = normalizeAuthorizationToken(localStorage.getItem(LEGACY_AUTH_TOKEN_KEY));
  if (legacy) {
    localStorage.setItem(AUTH_TOKEN_KEY, legacy);
    localStorage.removeItem(LEGACY_AUTH_TOKEN_KEY);
    return legacy;
  }

  return "";
};

export const setCurrentAuthToken = (token: string) => {
  const normalized = normalizeAuthorizationToken(token);
  if (!normalized) {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    return;
  }
  localStorage.setItem(AUTH_TOKEN_KEY, normalized);
};

export const getAdminAuthToken = (): string =>
  normalizeAuthorizationToken(localStorage.getItem(ADMIN_AUTH_TOKEN_KEY));

export const setAdminAuthToken = (token: string) => {
  const normalized = normalizeAuthorizationToken(token);
  if (!normalized) {
    localStorage.removeItem(ADMIN_AUTH_TOKEN_KEY);
    return;
  }
  localStorage.setItem(ADMIN_AUTH_TOKEN_KEY, normalized);
};

export const getTenantSwitchContext = (): TenantSwitchContext | null => {
  const raw = localStorage.getItem(TENANT_SWITCH_CONTEXT_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as TenantSwitchContext;
  } catch (error) {
    localStorage.removeItem(TENANT_SWITCH_CONTEXT_KEY);
    return null;
  }
};

export const isTenantSwitching = (): boolean => !!getTenantSwitchContext();

export const enterTenantSwitch = (context: TenantSwitchContext) => {
  localStorage.setItem(TENANT_SWITCH_CONTEXT_KEY, JSON.stringify(context));
};

export const clearTenantSwitchContext = () => {
  localStorage.removeItem(TENANT_SWITCH_CONTEXT_KEY);
};

export const exitTenantSwitch = () => {
  const context = getTenantSwitchContext();
  clearTenantSwitchContext();
  return context;
};

export const clearAuthContext = () => {
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(ADMIN_AUTH_TOKEN_KEY);
  localStorage.removeItem(LEGACY_AUTH_TOKEN_KEY);
  localStorage.removeItem(TENANT_SWITCH_CONTEXT_KEY);
};

// Compatibility exports for existing references.
export const REQUEST_ID_KEY = AUTH_TOKEN_KEY;
export const ADMIN_REQUEST_ID_KEY = ADMIN_AUTH_TOKEN_KEY;
export const resolveRequestIdFromUser = resolveTokenFromUser;
export const getCurrentRequestId = getCurrentAuthToken;
export const setCurrentRequestId = setCurrentAuthToken;
export const getAdminRequestId = getAdminAuthToken;
export const setAdminRequestId = setAdminAuthToken;
export const clearIdContext = clearAuthContext;
